import React from 'react';
import { useTheme } from '../lib/theme';
import { Badge, Card, SectionTitle, MetricRow, GaugeRing, ConfBar, OutcomeBadge, TimingBadge } from './ui';

// ============================================================
// TIMING INTELLIGENCE SECTION
// ============================================================
export function TimingIntelligence({ lead }) {
  var t = useTheme();
  if (!lead || !lead.timing) return null;
  var tm = lead.timing;

  var rows = [
    { label: 'Speed to Lead', value: tm.speed_to_lead_minutes != null ? Math.round(tm.speed_to_lead_minutes) + ' min' : 'N/A', rating: tm.speed_to_lead_rating },
    { label: 'First Response', value: tm.first_response_minutes != null ? Math.round(tm.first_response_minutes) + ' min' : 'N/A', rating: tm.first_response_rating },
    { label: 'Lead to Meeting Gap', value: tm.creation_to_meeting_days != null ? tm.creation_to_meeting_days + ' days' : 'N/A', rating: tm.creation_to_meeting_rating },
    { label: 'Momentum Gap', value: tm.momentum_gap_hours != null ? Math.round(tm.momentum_gap_hours) + ' hrs' : 'N/A', rating: tm.momentum_gap_rating },
  ];

  return React.createElement(Card, null,
    React.createElement(SectionTitle, null, 'Timing Intelligence'),
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 } },
      rows.map(function(r) {
        return React.createElement('div', { key: r.label, style: {
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '8px 12px', borderRadius: 6, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)',
        }},
          React.createElement('div', null,
            React.createElement('div', { style: { fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 } }, r.label),
            React.createElement('div', { style: { fontSize: 16, fontWeight: 700, color: '#fff', fontFamily: "'DM Mono', monospace" } }, r.value)
          ),
          r.rating && React.createElement(TimingBadge, { rating: r.rating })
        );
      })
    ),
    // Time of day
    (tm.lead_created_period || tm.first_response_period) && React.createElement('div', { style: { display: 'flex', gap: 16, marginTop: 12, fontSize: 11, color: 'rgba(255,255,255,0.4)' } },
      tm.lead_created_period && React.createElement('span', null, 'Lead created: ' + tm.lead_created_period.replace(/_/g, ' ')),
      tm.first_response_period && React.createElement('span', null, 'First response: ' + tm.first_response_period.replace(/_/g, ' '))
    ),
    // Timing risk summary
    tm.timing_risk_summary && React.createElement('div', { style: {
      marginTop: 12, padding: '10px 12px', borderRadius: 6, background: 'rgba(239,68,68,0.06)',
      borderLeft: '2px solid rgba(239,68,68,0.3)', fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.6,
    }}, tm.timing_risk_summary)
  );
}

// ============================================================
// FEEDBACK & OUTCOMES SECTION
// ============================================================
export function FeedbackOutcomes({ lead }) {
  if (!lead) return null;
  var hasAny = lead.actual_showed || lead.actual_closed;
  if (!hasAny) return null;

  return React.createElement(Card, null,
    React.createElement(SectionTitle, null, 'Outcomes & Feedback'),
    React.createElement('div', { style: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 } },
      React.createElement(OutcomeBadge, { showed: lead.actual_showed, closed: lead.actual_closed, dealAmount: lead.deal_amount })
    ),

    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 } },
      // Left: What happened
      React.createElement('div', null,
        React.createElement(MetricRow, { label: 'Showed Up', value: lead.actual_showed || '-', color: lead.actual_showed === 'TRUE' ? '#4ADE80' : lead.actual_showed === 'FALSE' ? '#EF4444' : 'rgba(255,255,255,0.3)' }),
        React.createElement(MetricRow, { label: 'Closed', value: lead.actual_closed || '-', color: lead.actual_closed === 'TRUE' ? '#4ADE80' : lead.actual_closed === 'FALSE' ? '#EF4444' : 'rgba(255,255,255,0.3)' }),
        lead.deal_amount && React.createElement(MetricRow, { label: 'Deal Amount', value: '$' + Number(lead.deal_amount).toLocaleString(), color: '#E8A838' }),
        lead.deal_tier && React.createElement(MetricRow, { label: 'Deal Tier', value: (lead.deal_tier || '').replace(/_/g, ' '), color: '#38BDF8' }),
        lead.lost_reason && React.createElement(MetricRow, { label: 'Lost Reason', value: lead.lost_reason, color: '#EF4444' })
      ),

      // Right: Prediction accuracy
      React.createElement('div', null,
        lead.show_prediction_accurate && React.createElement(MetricRow, {
          label: 'Show Prediction',
          value: lead.show_prediction_accurate === 'TRUE' ? 'CORRECT' : 'WRONG',
          color: lead.show_prediction_accurate === 'TRUE' ? '#4ADE80' : '#EF4444'
        }),
        lead.close_prediction_accurate && React.createElement(MetricRow, {
          label: 'Close Prediction',
          value: lead.close_prediction_accurate === 'TRUE' ? 'CORRECT' : 'WRONG',
          color: lead.close_prediction_accurate === 'TRUE' ? '#4ADE80' : '#EF4444'
        }),
        lead.prediction_error_direction && React.createElement(MetricRow, {
          label: 'Direction',
          value: lead.prediction_error_direction,
          color: lead.prediction_error_direction === 'accurate' ? '#4ADE80' : '#E8A838'
        }),
        lead.lead_gender && React.createElement(MetricRow, { label: 'Gender', value: lead.lead_gender }),
        lead.lead_source && React.createElement(MetricRow, { label: 'Source', value: lead.lead_source, color: 'rgba(255,255,255,0.5)' })
      )
    )
  );
}

// ============================================================
// REASONING CHAINS SECTION
// ============================================================
export function ReasoningChains({ lead }) {
  if (!lead || !lead.reasoning) return null;
  var r = lead.reasoning;
  if (!r.primary_driver && !r.secondary_driver) return null;

  return React.createElement(Card, null,
    React.createElement(SectionTitle, null, 'Show Probability Reasoning'),
    React.createElement('div', { style: { display: 'flex', gap: 16, marginBottom: 12 } },
      React.createElement('div', { style: { textAlign: 'center' } },
        React.createElement(GaugeRing, {
          value: lead.show_probability || 0, max: 1, size: 78,
          color: (lead.show_probability || 0) >= 0.7 ? '#4ADE80' : (lead.show_probability || 0) >= 0.5 ? '#E8A838' : '#EF4444',
          label: 'Base', sublabel: ''
        }),
      ),
      lead.show_decayed && React.createElement('div', { style: { textAlign: 'center' } },
        React.createElement(GaugeRing, {
          value: lead.show_probability_current || 0, max: 1, size: 78,
          color: '#EF4444', label: 'Current', sublabel: 'decayed'
        }),
      )
    ),
    r.primary_driver && React.createElement('div', { style: {
      padding: '10px 14px', borderRadius: 6, background: 'rgba(129,140,248,0.06)',
      borderLeft: '3px solid #818CF8', marginBottom: 8,
    }},
      React.createElement('div', { style: { fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 } }, 'Primary Driver'),
      React.createElement('div', { style: { fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 } }, r.primary_driver)
    ),
    r.secondary_driver && React.createElement('div', { style: {
      padding: '10px 14px', borderRadius: 6, background: 'rgba(255,255,255,0.02)',
      borderLeft: '3px solid rgba(255,255,255,0.1)',
    }},
      React.createElement('div', { style: { fontSize: 9, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 } }, 'Secondary Driver'),
      React.createElement('div', { style: { fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.5 } }, r.secondary_driver)
    ),
    lead.show_decay_reason && React.createElement('div', { style: { marginTop: 8, padding: '8px 12px', borderRadius: 4, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', fontSize: 11, color: '#EF4444' } },
      '\u26a0 Decay: ' + lead.show_decay_reason
    )
  );
}

// ============================================================
// REVENUE & TIER PREDICTION
// ============================================================
export function TierPrediction({ lead }) {
  if (!lead || !lead.tier_most_likely) return null;

  var tiers = [
    { key: 'coaching_only', label: 'Coaching ($10K)', prob: lead.tier_coaching_probability, color: '#4ADE80' },
    { key: 'medical_6mo', label: 'Medical 6mo ($20K)', prob: lead.tier_medical_6_probability, color: '#38BDF8' },
    { key: 'medical_12mo', label: 'Medical 12mo ($28K)', prob: lead.tier_medical_12_probability, color: '#818CF8' },
  ];

  return React.createElement(Card, null,
    React.createElement(SectionTitle, null, 'Revenue Prediction'),
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-around', marginBottom: 16 } },
      React.createElement('div', { style: { textAlign: 'center' } },
        React.createElement('div', { style: { fontSize: 28, fontWeight: 700, color: '#E8A838', fontFamily: "'DM Mono', monospace" } },
          lead.expected_revenue ? '$' + Math.round(lead.expected_revenue).toLocaleString() : '-'
        ),
        React.createElement('div', { style: { fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 4 } }, 'Expected Revenue')
      ),
      React.createElement('div', { style: { textAlign: 'center' } },
        React.createElement(Badge, { color: '#818CF8' }, (lead.tier_most_likely || '').replace(/_/g, ' ')),
        React.createElement('div', { style: { fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6 } }, 'Most Likely Tier')
      )
    ),
    tiers.map(function(tier) {
      var pct = (tier.prob || 0) * 100;
      return React.createElement('div', { key: tier.key, style: { display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0' } },
        React.createElement('span', { style: { fontSize: 11, color: 'rgba(255,255,255,0.5)', width: 140, flexShrink: 0 } }, tier.label),
        React.createElement('div', { style: { flex: 1, height: 8, background: 'rgba(255,255,255,0.04)', borderRadius: 4, overflow: 'hidden' } },
          React.createElement('div', { style: { width: pct + '%', height: '100%', background: tier.color, borderRadius: 4 } })
        ),
        React.createElement('span', { style: { fontSize: 11, color: tier.color, fontFamily: "'DM Mono', monospace", width: 40, textAlign: 'right' } }, Math.round(pct) + '%')
      );
    })
  );
}
